import React, { useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { GlobalState } from '@/stores/reducer'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  usePostAudioMutation,
  usePatchYakurekiEventMutation,
} from '@/features/yakureki/hooks/queries/useAudioMutations'
import { useMyPharmacyQuery } from '@/features/yakureki/hooks/queries/useMyPharmacyQuery'
import SimpleRecordButton from '@/apps/legacy/components/molecules/SimpleRecordButton'
import { useMicrophonePermission } from '@/features/yakureki/hooks/useMicrophonePermission'
import { useRecordingState } from '@/features/yakureki/hooks/useRecordingState'
import type { RecordingEngineReturn } from '@/features/yakureki/recording/types'
import { useRecordingEngine } from '@/features/yakureki/recording/useRecordingEngine'
import { NewRecordButton } from '@/features/yakureki/components/RecordButton/NewRecordButton'

interface UseRecordAudioReturn {
  isNewRecording: boolean
  isTargetRecording: boolean
  newAudioURL: string | null
  targetAudioURL: string | null
  newRecordButton: JSX.Element
  simpleTargetRecordButton: JSX.Element
  isSending: boolean
  handleKeyDownForNewRecord: (event: KeyboardEvent) => void
  handleKeyDownForRecord: (event: KeyboardEvent) => void
  mediaRecorder: MediaRecorder | null
  cancelRecording: () => void
  cancelNewRecording: () => void
  // 既存患者の録音開始を外部 caller (= deep-link auto-start) から発火するための API.
  // streaming / batch の分岐は内部で判定し、 既に録音中・送信済なら no-op.
  startTargetRecording: () => void
  // streaming engine pass-through (= caller page で fallback / failed の banner mount 用).
  // hook が内部に保持する単一 streaming instance をそのまま expose する.
  streamingEngine: RecordingEngineReturn
}

/**
 * 録音機能を提供する統合フック
 *
 * TODO(future): hook 物理分離 + Context dispatcher 化 (C' 方針)。
 * 現状は内部 flag 分岐 (useStreamingForTarget) で legacy/streaming を切替えているが、
 * streaming 側の独自拡張 (transcript 即時表示等) が本格化した時に C' 化する。
 * 詳細: log/93_20260424_streaming_ui_foundation/design/namespace_and_overlay.md §4.1
 *
 * 内部で以下のフックを使用:
 * - useMicrophonePermission: マイク権限管理
 * - useRecordingState: 録音状態管理
 * - useRecordingEngine: streaming 経路 (既存患者 + transcription_mode=streaming のみ)
 * - useMyPharmacyQuery: transcription_mode の取得
 *
 * streaming 経路は **既存患者 (yakurekiId 指定あり) のみ** 対象。
 * - 新規録音 (yakurekiId なし) は streaming 不可 (stream/start が yakureki_id 必須)
 *   → 常に既存の MediaRecorder 経路 (batch) を使う
 * - 既存患者で transcription_mode=streaming の薬局のみ streaming 経路
 * - streaming 失敗時の fallbackBlob は既存 patchEventMutation に流す
 *
 * @param name - 患者名
 * @param setName - 患者名設定関数
 * @param closeModal - モーダルを閉じる関数
 * @param yakurekiId - 既存患者の場合のID
 * @param pharmacistId - 担当薬剤師ID
 * @param memo - 任意のメモ（送信時に memoItems として添付。空なら省略）
 * @param setMemo - メモリセット用セッター（送信後・キャンセル時に呼ぶ）
 * @param hasCompanionOrSubstitute - 対象患者の家族 (同伴/代理) 有無 (streaming 経路で stream/start に渡る)
 */
export const useRecordAudio = (
  name: string,
  setName: (name: string) => void,
  closeModal: () => void,
  yakurekiId?: string,
  pharmacistId?: string,
  memo?: string,
  setMemo?: (memo: string) => void,
  hasCompanionOrSubstitute?: boolean
): UseRecordAudioReturn => {
  const isLoggedIn = useSelector((state: GlobalState) => state.isLoggedIn)
  const isUseNsips = localStorageWrapper.getItem('IsUseNsips') || false

  // transcription_mode (未取得時は 'batch' にフォールバックして既存動作を壊さない)
  const { transcriptionMode } = useMyPharmacyQuery()
  // streaming 経路は「既存患者 + streaming モード」の組合せでのみ有効
  const useStreamingForTarget =
    transcriptionMode === 'streaming' && !!yakurekiId

  // TanStack Query Mutations
  const postAudioMutation = usePostAudioMutation()
  const patchEventMutation = usePatchYakurekiEventMutation()
  const queryClient = useQueryClient()

  // マイク権限管理
  const { permissionStatus, mediaRecorder, requestMicrophoneAccess } =
    useMicrophonePermission(isLoggedIn)

  // 新規録音の状態管理 (常に batch)
  const newRecording = useRecordingState(
    mediaRecorder,
    permissionStatus,
    requestMicrophoneAccess
  )

  // 既存患者録音の状態管理 (batch 経路)
  const targetRecording = useRecordingState(
    mediaRecorder,
    permissionStatus,
    requestMicrophoneAccess
  )

  // 送信中状態
  const [isSending, setIsSending] = React.useState(false)

  // 録音開始時点の context を snapshot として ref に握り、 fallback commit 時に同値を使う.
  // memo / name は caller の state に従って変化するが、 fallback POST は録音開始時の値を
  // 採用する (= 録音中に memo を書換えても録音 audio の指導コンテキストは start 時点が真).
  const fallbackSnapshotRef = React.useRef<{
    label: string
    pharmacistId?: string
    memoItems: string[]
  }>({ label: '', pharmacistId: undefined, memoItems: [] })

  // streaming 経路 (useStreamingForTarget=true の時のみ invoke される).
  // onFallbackCommit: fallback 経路 stop 時に hook 内で legacy batch (= PATCH /api/yakureki/{id})
  // に blob を流す. caller 側 useEffect の fallbackBlob 拾いは廃止し、 「state='completed' =
  // commit 完了」 invariant を strict に保つ.
  const streaming = useRecordingEngine({
    onFallbackCommit: useCallback(
      async (blob: Blob): Promise<void> => {
        if (!yakurekiId) {
          throw new Error('yakurekiId missing for legacy fallback commit')
        }
        const file = new File([blob], 'audio.mp4', {
          type: blob.type || 'audio/mp4',
        })
        const snap = fallbackSnapshotRef.current
        await patchEventMutation.mutateAsync({
          audioSource: file,
          label: snap.label,
          yakurekiId,
          pharmacistId: snap.pharmacistId,
          memoItems: snap.memoItems,
        })
      },
      [yakurekiId, patchEventMutation]
    ),
  })

  // streaming 状態を既存患者向けに橋渡し
  const isStreamingActive =
    streaming.state === 'starting' ||
    streaming.state === 'recording' ||
    streaming.state === 'fallback'
  const isStreamingFinalizing = streaming.state === 'finalizing'

  const effectiveIsTargetRecording = useStreamingForTarget
    ? isStreamingActive
    : targetRecording.isRecording
  const effectiveIsSending =
    isSending || (useStreamingForTarget && isStreamingFinalizing)

  // 新規録音の送信 (batch のみ、streaming 非対応)
  const sendNewAudio = useCallback(async () => {
    setIsSending(true)
    const audioURLToSend = newRecording.audioURL
    const memoItems = memo && memo.trim() ? [memo.trim()] : []

    // UIを先に更新（モーダルを閉じる）
    // 注意: clearAudio() は URL.revokeObjectURL() を呼ぶため、
    // API 呼び出し前に実行してはいけない
    setName('')
    setMemo?.('')
    closeModal()

    if (audioURLToSend) {
      try {
        await postAudioMutation.mutateAsync({
          audioSource: audioURLToSend,
          label: name,
          pharmacistId,
          memoItems,
        })
      } catch {
        // エラーハンドリングはMutation内で行われる
      } finally {
        // API 呼び出し完了後に URL を解放
        URL.revokeObjectURL(audioURLToSend)
      }
    }

    // API 呼び出し後に状態をクリア
    // （clearAudio 内の revokeObjectURL は既に解放済みなので no-op）
    newRecording.clearAudio()
    setIsSending(false)
  }, [
    newRecording,
    setName,
    setMemo,
    closeModal,
    postAudioMutation,
    name,
    pharmacistId,
    memo,
  ])

  // 既存患者への録音送信 (batch 経路)
  const sendExistingAudio = useCallback(async () => {
    setIsSending(true)
    const audioURLToSend = targetRecording.audioURL
    const memoItems = memo && memo.trim() ? [memo.trim()] : []

    console.info(
      '[transcription-dispatch] useRecordAudio.sendExistingAudio: legacy batch path',
      { yakurekiId, transcriptionMode }
    )

    // UIを先に更新（モーダルを閉じる）
    // 注意: clearAudio() は URL.revokeObjectURL() を呼ぶため、
    // API 呼び出し前に実行してはいけない
    setName('')
    setMemo?.('')
    closeModal()

    if (audioURLToSend && yakurekiId) {
      try {
        await patchEventMutation.mutateAsync({
          audioSource: audioURLToSend,
          label: name,
          yakurekiId,
          pharmacistId,
          memoItems,
        })
      } catch {
        // エラーハンドリングはMutation内で行われる
      } finally {
        // API 呼び出し完了後に URL を解放
        URL.revokeObjectURL(audioURLToSend)
      }
    }

    // API 呼び出し後に状態をクリア
    // （clearAudio 内の revokeObjectURL は既に解放済みなので no-op）
    targetRecording.clearAudio()
    setIsSending(false)
  }, [
    targetRecording,
    setName,
    setMemo,
    closeModal,
    patchEventMutation,
    name,
    yakurekiId,
    pharmacistId,
    memo,
    transcriptionMode,
  ])

  // streaming 経路: start
  const startStreamingForTarget = useCallback(async () => {
    if (!yakurekiId) return
    const memoItemsArr = memo && memo.trim() ? [memo.trim()] : []
    const memoItems = memoItemsArr.length > 0 ? memoItemsArr : undefined
    const hasCompanionMap = hasCompanionOrSubstitute
      ? { [yakurekiId]: true }
      : undefined
    // fallback 経路用の snapshot を握る (= 録音開始時点の値で commit する).
    fallbackSnapshotRef.current = {
      label: name,
      pharmacistId,
      memoItems: memoItemsArr,
    }
    console.info(
      '[transcription-dispatch] useRecordAudio.start: streaming path',
      { yakurekiId, transcriptionMode, hasCompanionOrSubstitute }
    )
    await streaming.start({
      yakurekiIds: [yakurekiId],
      pharmacistId,
      memoItems,
      hasCompanionOrSubstituteMap: hasCompanionMap,
    })
  }, [
    streaming,
    yakurekiId,
    pharmacistId,
    memo,
    name,
    transcriptionMode,
    hasCompanionOrSubstitute,
  ])

  // streaming 経路: stop (finalize に audio multipart 同梱、 1-signal で完結)
  //
  // legacy の patchYakurekiEvent mutation と対称性を取り、stop 成功時に
  // patientList + patientDetail を invalidate する。2s polling を待たずに
  // backend 側の status=PROCESSING を front に反映し、overlay 表示と
  // patientDetail 更新が deterministic になる。
  const stopStreamingForTarget = useCallback(async () => {
    await streaming.stop()
    queryClient.invalidateQueries({ queryKey: queryKeys.patients.all })
    if (yakurekiId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
    }
    // completed 遷移は useEffect 側で検知して UI 更新する
  }, [streaming, queryClient, yakurekiId])

  // streaming 完了後の UI cleanup. fallback 経路の batch POST は onFallbackCommit (上記) で
  // hook 内に閉じ込め済のため、 caller 側は UI 閉じ + reset() だけ行う.
  // 「state='completed' = back に audio が届いた」 invariant が hook 側で担保される.
  useEffect(() => {
    if (!useStreamingForTarget) return
    if (streaming.state !== 'completed') return
    setName('')
    setMemo?.('')
    closeModal()
    // 完了後 cleanup (backend に再度 cancel を投げると finalize 済 SS が CANCELLED
    // 上書きされるため reset = local-only を使う).
    streaming.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming.state, useStreamingForTarget])

  // 録音完了後の自動送信 (batch 経路のみ)
  useEffect(() => {
    if (useStreamingForTarget) return
    if (
      targetRecording.audioURL &&
      !targetRecording.isRecording &&
      !isSending &&
      yakurekiId &&
      !targetRecording.isCancelled
    ) {
      const timer = setTimeout(() => {
        sendExistingAudio()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [
    targetRecording.audioURL,
    targetRecording.isRecording,
    targetRecording.isCancelled,
    isSending,
    yakurekiId,
    sendExistingAudio,
    useStreamingForTarget,
  ])

  // キーボードイベントハンドラー（新規録音用、常に batch）
  const handleKeyDownForNewRecord = useCallback(
    (event: KeyboardEvent) => {
      if (isUseNsips) return
      if (event.key === 'Enter' && event.shiftKey) {
        if (newRecording.isRecording) {
          newRecording.stopRecording()
        } else if (newRecording.audioURL) {
          sendNewAudio()
        } else {
          newRecording.startRecording()
        }
      } else if (event.key === 'Backspace' && event.shiftKey) {
        if (newRecording.audioURL) {
          newRecording.clearAudio()
        }
      }
    },
    [isUseNsips, newRecording, sendNewAudio]
  )

  // キーボードイベントハンドラー（既存患者録音用、streaming / batch 自動切替）
  const handleKeyDownForRecord = useCallback(
    (event: KeyboardEvent) => {
      if (!isUseNsips) return
      if (event.key === 'Enter' && event.shiftKey) {
        if (useStreamingForTarget) {
          if (isStreamingActive) {
            stopStreamingForTarget()
          } else if (streaming.state === 'idle') {
            startStreamingForTarget()
          }
          // finalizing / completed / failed は無視
          return
        }
        if (targetRecording.isRecording) {
          targetRecording.stopRecording()
        } else if (targetRecording.audioURL) {
          sendExistingAudio()
        } else {
          targetRecording.startRecording()
        }
      } else if (event.key === 'Backspace' && event.shiftKey) {
        if (useStreamingForTarget) {
          if (isStreamingActive) void streaming.cancel()
          return
        }
        if (targetRecording.audioURL) {
          targetRecording.clearAudio()
        }
      }
    },
    [
      isUseNsips,
      useStreamingForTarget,
      isStreamingActive,
      streaming,
      startStreamingForTarget,
      stopStreamingForTarget,
      targetRecording,
      sendExistingAudio,
    ]
  )

  // 既存患者向け録音開始 (外部 caller 用、simpleTargetRecordButton の onClick と
  // 同じ start 経路を選ぶが、 stop / send は触らない).
  const startTargetRecording = useCallback(() => {
    if (!yakurekiId) return
    if (useStreamingForTarget) {
      if (streaming.state === 'idle') {
        startStreamingForTarget()
      }
      return
    }
    if (targetRecording.isRecording) return
    if (targetRecording.audioURL) return
    targetRecording.startRecording()
  }, [
    yakurekiId,
    useStreamingForTarget,
    streaming.state,
    startStreamingForTarget,
    targetRecording,
  ])

  // 新規録音ボタン（JSX、常に batch）
  const newRecordButton = (
    <NewRecordButton
      audioURL={newRecording.audioURL}
      audioRef={newRecording.audioRef}
      isRecording={newRecording.isRecording}
      isAutoStopped={newRecording.isAutoStopped}
      permissionStatus={permissionStatus}
      onStartRecording={newRecording.startRecording}
      onStopRecording={newRecording.stopRecording}
      onClearAudio={newRecording.clearAudio}
      onSendAudio={sendNewAudio}
    />
  )

  // シンプル録音ボタン（JSX、既存患者: streaming / batch 自動切替）
  const simpleTargetRecordButton = (
    <SimpleRecordButton
      isRecording={effectiveIsTargetRecording}
      onClick={() => {
        if (useStreamingForTarget) {
          if (isStreamingActive) {
            stopStreamingForTarget()
          } else if (streaming.state === 'idle') {
            startStreamingForTarget()
          }
          return
        }
        if (targetRecording.isRecording) {
          targetRecording.stopRecording()
        } else if (targetRecording.audioURL) {
          sendExistingAudio()
        } else {
          targetRecording.startRecording()
        }
      }}
      onStopRecording={
        useStreamingForTarget
          ? stopStreamingForTarget
          : targetRecording.stopRecording
      }
      onCancelRecording={
        useStreamingForTarget
          ? streaming.cancel
          : targetRecording.cancelRecording
      }
      disabled={permissionStatus !== 'granted' || effectiveIsSending}
      size="medium"
    />
  )

  return {
    isNewRecording: newRecording.isRecording,
    isTargetRecording: effectiveIsTargetRecording,
    newAudioURL: newRecording.audioURL,
    // streaming モードでは audioURL は存在しない (transcript は backend で確定済)
    targetAudioURL: useStreamingForTarget ? null : targetRecording.audioURL,
    newRecordButton,
    simpleTargetRecordButton,
    isSending: effectiveIsSending,
    handleKeyDownForNewRecord,
    handleKeyDownForRecord,
    mediaRecorder,
    cancelRecording: useStreamingForTarget
      ? streaming.cancel
      : targetRecording.cancelRecording,
    cancelNewRecording: newRecording.cancelRecording,
    startTargetRecording,
    streamingEngine: streaming,
  }
}
